import { CONFIG_CATEGORIES, diffLines, rebindCfg, type CfgFile, type Config, type ConfigCategory } from "@69ly/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { BackLink } from "../components/BackLink.tsx";
import { CfgTable } from "../components/CfgTable.tsx";
import { keysQuery, KeysCard, useRebinds } from "../components/KeysCard.tsx";
import { packBody, PackEditor, type PackFile } from "../components/PackEditor.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { useConfig, useConfigVersion, useDeleteConfig, useNewConfigVersion, useUpdateConfig } from "../lib/config-queries.ts";
import { errorText } from "../lib/errors.ts";
import { InstallCard } from "./Configs.tsx";

/** What changed between two versions, line by line. */
function Changes({ before, after }: { before: string; after: string }) {
  const { t } = useTranslation();
  const diff = diffLines(before, after).filter((d) => d.kind !== "same");
  if (diff.length === 0) return <p className="muted small">{t("configs.noChanges")}</p>;
  return (
    <ul className="cfg-diff mono small">
      {diff.map((d, i) => (
        <li key={i} className={`diff-${d.kind}`}>
          <span aria-hidden="true">{d.kind === "added" ? "+" : "−"}</span>
          <span className="visually-hidden">{t(`configs.${d.kind}`)}</span> {d.text || " "}
        </li>
      ))}
    </ul>
  );
}

function EditMeta({ config, onDone }: { config: Config; onDone: () => void }) {
  const { t } = useTranslation();
  const update = useUpdateConfig(config.id);
  const [title, setTitle] = useState(config.title);
  const [description, setDescription] = useState(config.description);
  const [category, setCategory] = useState<ConfigCategory>(config.category);
  return (
    <form
      className="card config-edit"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate({ title, description, category, overrides: config.overrides }, { onSuccess: onDone });
      }}
    >
      <label className="field">
        <span>{t("form.titleLabel")}</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={60} required />
      </label>
      <label className="field">
        <span>{t("configs.categoryLabel")}</span>
        <select value={category} onChange={(e) => setCategory(e.target.value as ConfigCategory)}>
          {CONFIG_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {t(`configs.category.${c}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>{t("configs.description")}</span>
        <textarea rows={3} value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
      </label>
      {update.error && <p className="field-error small">{errorText(t, update.error)}</p>}
      <div className="row">
        <button type="submit" className="btn btn-primary" disabled={update.isPending}>
          {t("configs.saveDetails")}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onDone}>
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
}

/** The files of a version, main first, by name. */
const packFiles = (fileName: string, text: string, files: CfgFile[]): PackFile[] => [{ name: fileName, text }, ...files];

function NewVersion({ config, onDone }: { config: Config; onDone: () => void }) {
  const { t } = useTranslation();
  const upload = useNewConfigVersion(config.id);
  const before = packFiles(config.fileName, config.text, config.files);
  const [files, setFiles] = useState<PackFile[]>(before);
  const [active, setActive] = useState(0);
  const now = files[active] ?? files[0];
  const name = (f: PackFile, i: number) => (i === 0 ? f.name.trim() || config.fileName : f.name.trim()).toLowerCase();
  const was = before.find((f, i) => name(f, i) === name(now, active));
  const unchanged = JSON.stringify(files.map((f, i) => [name(f, i), f.text])) === JSON.stringify(before.map((f, i) => [name(f, i), f.text]));
  const ready = files.every((f, i) => f.text.trim() && (i === 0 || f.name.trim()));
  return (
    <form
      className="card config-edit"
      onSubmit={(e) => {
        e.preventDefault();
        upload.mutate(packBody(files), { onSuccess: onDone });
      }}
    >
      <h2 className="panel-title">{t("configs.newVersionTitle", { version: config.version + 1 })}</h2>
      <PackEditor files={files} onChange={setFiles} active={active} onActive={setActive} mainHint={config.fileName} />
      <h3 className="eyebrow">{files.length > 1 ? t("configs.whatChangesIn", { name: now.name || config.fileName }) : t("configs.whatChanges")}</h3>
      {was ? <Changes before={was.text} after={now.text} /> : <p className="muted small">{t("configs.newFile")}</p>}
      {upload.error && <p className="field-error small">{errorText(t, upload.error)}</p>}
      <div className="row">
        <button type="submit" className="btn btn-primary" disabled={upload.isPending || unchanged || !ready}>
          {t("configs.saveVersion")}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onDone}>
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
}

function ConfigView({ config }: { config: Config }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const remove = useDeleteConfig();
  const update = useUpdateConfig(config.id);
  const [mode, setMode] = useState<"read" | "edit" | "version">("read");
  const [picked, setPicked] = useState<number | null>(null);
  const viewing = picked && picked !== config.version ? picked : null;
  const older = useConfigVersion(config.id, viewing);
  const previous = useConfigVersion(config.id, config.versions.length > 1 ? (viewing ?? config.version) - 1 : null);
  // The files of the version on screen, main first, and the one being read.
  const shownFiles = viewing ? packFiles(config.fileName, older.data?.text ?? "", older.data?.files ?? []) : packFiles(config.fileName, config.text, config.files);
  const [openFile, setOpenFile] = useState(0);
  const fileIndex = Math.min(openFile, shownFiles.length - 1);
  const names = shownFiles.map((f) => f.name);
  const text = shownFiles[fileIndex]?.text ?? "";
  const prevFile = previous.data ? packFiles(config.fileName, previous.data.text, previous.data.files ?? []).find((f) => f.name.toLowerCase() === names[fileIndex]?.toLowerCase()) : undefined;
  const isPack = shownFiles.length > 1;
  const openByName = (n: string) => setOpenFile(Math.max(0, names.findIndex((x) => x.toLowerCase() === n.toLowerCase())));
  const date = (iso: string) => new Date(iso).toLocaleString(i18n.language, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  const shownVersion = viewing ?? config.version;
  // The player's own keys: the reading and the download use them.
  const [keys, setKeys] = useRebinds(`69ly.keys.${config.id}`);
  const shownText = rebindCfg(text, keys);

  const saveOwn = (command: string, words: string) => {
    const overrides = { ...config.overrides };
    if (words) overrides[command] = words;
    else delete overrides[command];
    update.mutate({ title: config.title, description: config.description, category: config.category, overrides });
  };

  return (
    <div className="page config-detail">
      <div>
        <BackLink fallback="/configs" fallbackLabel={t("configs.title")} />
        <p className="eyebrow">{t(`configs.category.${config.category}`)}</p>
        <div className="page-head">
          <div>
            <h1 className="page-title">{config.title}</h1>
            <p className="muted">
              {t("configs.byLine", { name: config.author.name, version: config.version, date: date(config.updatedAt) })}
            </p>
            {config.description && <p className="config-description">{config.description}</p>}
          </div>
          {config.canEdit && mode === "read" && (
            <div className="row">
              <button type="button" className="btn btn-secondary" onClick={() => setMode("version")}>
                {t("configs.newVersion")}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setMode("edit")}>
                {t("mapView.edit")}
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-danger"
                onClick={() => {
                  if (window.confirm(t("configs.confirmDelete", { title: config.title }))) remove.mutate(config.id, { onSuccess: () => navigate("/configs", { replace: true }) });
                }}
              >
                {t("detail.delete")}
              </button>
            </div>
          )}
        </div>
      </div>

      {mode === "edit" && <EditMeta config={config} onDone={() => setMode("read")} />}
      {mode === "version" && <NewVersion config={config} onDone={() => setMode("read")} />}

      <div className="columns">
        <div className="column-main">
          <section className="card">
            {viewing && (
              <p className="banner banner-info small">
                {t("configs.viewingOld", { version: viewing })}{" "}
                <button type="button" className="link-button" onClick={() => setPicked(null)}>
                  {t("configs.backToCurrent")}
                </button>
              </p>
            )}
            {isPack && (
              <div className="pack-tabs" role="tablist" aria-label={t("configs.packFiles")}>
                {shownFiles.map((f, i) => (
                  <button key={f.name} type="button" role="tab" aria-selected={i === fileIndex} className="pack-tab" onClick={() => setOpenFile(i)}>
                    <span className="mono">{f.name}</span>
                    {i === 0 && <span className="pack-main-tag">{t("configs.mainTag")}</span>}
                  </button>
                ))}
              </div>
            )}
            {viewing && older.isPending ? (
              <Loading />
            ) : (
              <CfgTable
                key={names[fileIndex]}
                text={shownText}
                overrides={config.overrides}
                onOverride={config.canEdit && !viewing ? saveOwn : undefined}
                pack={isPack ? { names, open: openByName } : undefined}
              />
            )}
          </section>
        </div>
        <aside className="column-side">
          <KeysCard files={shownFiles} overrides={config.overrides} value={keys} onChange={setKeys} />
          <InstallCard
            fileName={config.fileName}
            downloadUrl={`/api/configs/${config.id}/download${viewing ? `?v=${viewing}` : ""}${keysQuery(keys, viewing ? "&" : "?")}`}
            text={shownText}
            pack={
              isPack
                ? {
                    count: shownFiles.length,
                    fileName: names[fileIndex],
                    fileUrl: `/api/configs/${config.id}/download?file=${encodeURIComponent(names[fileIndex])}${viewing ? `&v=${viewing}` : ""}${keysQuery(keys, "&")}`,
                  }
                : undefined
            }
          />
          {config.versions.length > 1 && (
            <section className="card">
              <h2 className="panel-title">{t("configs.versions")}</h2>
              <ul className="config-versions">
                {config.versions.map((v) => (
                  <li key={v.version}>
                    <button type="button" className={`callout-row${v.version === shownVersion ? " is-selected" : ""}`} onClick={() => setPicked(v.version)}>
                      <span>
                        v{v.version}
                        {v.version === config.version && <span className="muted small"> · {t("configs.current")}</span>}
                      </span>
                      <span className="muted small">
                        {v.author.name} · {date(v.createdAt)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {shownVersion > 1 && previous.data && (
                <>
                  <h3 className="eyebrow">{isPack ? t("configs.changesFromIn", { version: shownVersion - 1, name: names[fileIndex] }) : t("configs.changesFrom", { version: shownVersion - 1 })}</h3>
                  {prevFile ? <Changes before={prevFile.text} after={text} /> : <p className="muted small">{t("configs.newFile")}</p>}
                </>
              )}
            </section>
          )}
          <p className="muted small">
            <Link to="/configs">{t("configs.backToShelf")}</Link>
          </p>
        </aside>
      </div>
    </div>
  );
}

export function ConfigDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const config = useConfig(id);
  if (config.isPending) return <Loading />;
  if (config.error) return <ErrorState message={errorText(t, config.error)} onRetry={() => void config.refetch()} />;
  return <ConfigView key={config.data.id} config={config.data} />;
}
